import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Keyboard, Pagination, Zoom } from 'swiper';
import ZoomButton from './ZoomButton/ZoomButton';

/* eslint-disable import/no-unresolved */
import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
/* eslint-enable */

const Carousel = ({
  paginationClassName,
  paginationEnabled,
  slides,
  zoomEnabled = true,
}) => {
  // zoom
  const swiperRef = useRef(null);
  const [swiperIsZoomed, setSwiperIsZoomed] = useState(null);
  const [zoomBtnIsHidden, setZoomBtnIsHidden] = useState(false);

  // zoom in/out current slide when "zoom" icon is toggled
  useEffect(() => {
    if (swiperIsZoomed) {
      swiperRef.current.swiper.zoom.in();
    } else {
      swiperRef.current.swiper.zoom.out();
    }
  }, [swiperIsZoomed]);

  // return markup based on slide type
  const getSlideMarkup = (slide, i) => {
    // image type
    if (slide.type === 'image') {
      return (
        // NOTE: .swiper-zoom-container is specific to swiper and required to zoom
        <div className="carousel-image-slide">
          <div className={classnames({ 'swiper-zoom-container': zoomEnabled })}>
            <Image
              alt={slide.alt}
              height={300}
              itemProp="image"
              priority={i === 0}
              src={slide.src}
              width={600}
            />
          </div>
        </div>
      );
    }

    // video type
    return (
      <div className="carousel-video-slide">
        <div className="carousel-video-responsive">
          <iframe
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen="allowfullscreen"
            height="315"
            src={slide.src}
            title={slide.alt}
            width="560"
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Swiper
        ally={{
          enabled: true,
          paginationBulletMessage: 'Go to slide {{index}}',
        }}
        grabCursor
        keyboard={{
          enabled: true,
          onlyInViewport: true,
          pageUpDown: true,
        }}
        modules={[A11y, Keyboard, Pagination, Zoom]}
        onSlideChange={(swiperCore) => {
          if (zoomEnabled) {
            const { realIndex } = swiperCore;

            // zoom out
            if (swiperIsZoomed) setSwiperIsZoomed(false);

            // test to prevent re-renders
            // show or hide the zoom btn if the
            // slide contains an image or not
            if (slides[realIndex]?.type !== 'image') {
              setZoomBtnIsHidden(true);
            } else if (zoomBtnIsHidden) {
              setZoomBtnIsHidden(false);
            }
          }
        }}
        pagination={{
          clickable: true,
          el: `.${paginationClassName}`,
          enabled: paginationEnabled,
        }}
        ref={swiperRef}
        zoom={{
          enabled: zoomEnabled,
          maxRatio: 2.5,
          toggle: false,
        }}
      >
        {slides?.map((slide, i) => {
          return (
            <SwiperSlide key={slide.src}>
              {getSlideMarkup(slide, i)}
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div className={paginationClassName} />
      {zoomEnabled && (
        <ZoomButton
          swiperIsZoomed={swiperIsZoomed}
          setSwiperIsZoomed={setSwiperIsZoomed}
          zoomBtnIsHidden={zoomBtnIsHidden}
        />
      )}
    </>
  );
};

Carousel.propTypes = {
  paginationClassName: PropTypes.string.isRequired,
  slides: PropTypes.arrayOf(PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
    type: PropTypes.oneOf([
      'image',
      'video',
    ]),
  })).isRequired,
  zoomEnabled: PropTypes.bool,
};

export default Carousel;
